// components/Store/EmoticonStore.jsx

import { useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Card, CardMedia, CardContent,
    Button, CircularProgress, Accordion, AccordionSummary,
    AccordionDetails, Pagination, Stack, TextField, Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import api from '../../api/axios';
import { useUser } from '../../contexts/UserContext';

const getFullImageUrl = (url) =>
    url?.startsWith('http') ? url : `https://sandle.it.com${url}`;

const MOBILE_PAGE_SIZE = 5;
const DESKTOP_PAGE_SIZE = 15;

const EmoticonStore = () => {
    const { userInfo, refreshUserInfo } = useUser();
    const [emoticons, setEmoticons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [buyLoadingId, setBuyLoadingId] = useState(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const pageSize = isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE;

    const fetchEmoticons = async () => {
        try {
            const res = await api.get('/api/rewards/emoticons/shop/');
            setEmoticons(res.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchEmoticons(); }, []);

    // 검색/모드 변경 시 페이지 튀는 거 방지
    useEffect(() => { setPage(1); }, [search, isMobile]);

    const handleBuy = async (id, price) => {
        if (!userInfo) return;
        if (userInfo.points < price) {
            alert('Leaf🌿가 부족합니다!');
            return;
        }

        try {
            setBuyLoadingId(id);
            await api.post(`/api/rewards/emoticons/buy/${id}/`);
            await refreshUserInfo();
            await fetchEmoticons();
            alert('구매 완료!');
        } catch (err) {
            alert(err.response?.data?.detail || '구매 실패');
        } finally {
            setBuyLoadingId(null);
        }
    };

    const filtered = useMemo(() => {
        const q = (search || '').trim();
        if (!q) return emoticons;
        return emoticons.filter((e) => (e?.name || '').includes(q));
    }, [emoticons, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

    // 혹시 page가 범위를 넘어가면 보정
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const paginatedItems = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, page, pageSize]);

    const handlePageChange = (_, newPage) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loading) return <CircularProgress />;

    const stockText = (e) => (e.is_unlimited ? '무제한' : `재고: ${e.stock ?? 0}개`);

    const renderThumb = (e, height = 110) => {
        if (e.image) {
            return (
                <CardMedia
                    component="img"
                    image={getFullImageUrl(e.image)}
                    alt={e.name}
                    sx={{ height, objectFit: 'contain' }}
                />
            );
        }
        return (
            <Box display="flex" alignItems="center" justifyContent="center" sx={{ height }}>
                <Typography fontSize={36}>{e.emoji_text || '❔'}</Typography>
            </Box>
        );
    };

    return (
        <Box p={2}>
            {!isMobile && (
                <Typography variant="h5" gutterBottom>
                    😊 이모티콘 상점
                </Typography>
            )}

            {isMobile && (
                <TextField
                    fullWidth
                    size="small"
                    placeholder="이모티콘 검색"
                    variant="outlined"
                    value={search}
                    onChange={(ev) => setSearch(ev.target.value)}
                    sx={{ mb: 2 }}
                />
            )}

            {/* ✅ MOBILE: 아코디언 + 페이지네이션(5개/페이지) */}
            {isMobile ? (
                <>
                    {paginatedItems.map((e) => {
                        const isSoldOut = !e.is_unlimited && (e.stock ?? 0) <= 0;
                        const isOwned = !!e.is_owned;
                        const buyDisabled =
                            isSoldOut ||
                            isOwned ||
                            (userInfo?.points ?? 0) < e.price ||
                            buyLoadingId === e.id;

                        return (
                            <Accordion
                                key={e.id}
                                sx={{
                                    mb: 1,
                                    opacity: isSoldOut ? 0.55 : 1,
                                }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        width="100%"
                                        gap={1}
                                        sx={{ minWidth: 0 }}
                                    >
                                        <Box display="flex" flexDirection="column" sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography fontWeight="bold" noWrap>
                                                {e.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {e.price} Leaf🌿
                                            </Typography>
                                        </Box>

                                        <Box display="flex" gap={0.6} sx={{ flexShrink: 0 }}>
                                            {isSoldOut && <Chip size="small" label="품절" />}
                                            {isOwned && <Chip size="small" label="보유중" />}
                                        </Box>
                                    </Box>
                                </AccordionSummary>

                                <AccordionDetails>
                                    <Box mb={1} textAlign="center">
                                        {e.image ? (
                                            <img
                                                src={getFullImageUrl(e.image)}
                                                alt={e.name}
                                                style={{
                                                    width: '100%',
                                                    height: 140,
                                                    objectFit: 'contain',
                                                    borderRadius: 8,
                                                }}
                                            />
                                        ) : (
                                            <Box display="flex" alignItems="center" justifyContent="center" height={140} fontSize={36}>
                                                {e.emoji_text || '❔'}
                                            </Box>
                                        )}
                                    </Box>

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {stockText(e)}
                                    </Typography>

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="medium"
                                        disabled={buyDisabled}
                                        onClick={() => handleBuy(e.id, e.price)}
                                    >
                                        {isSoldOut ? '품절' : isOwned ? '보유중' : buyLoadingId === e.id ? '구매중…' : '구매'}
                                    </Button>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}

                    {totalPages > 1 && (
                        <Stack mt={2} alignItems="center">
                            <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
                        </Stack>
                    )}
                </>
            ) : (
                <>
                    {/* ✅ DESKTOP: 5개/줄 고정(CSS Grid) + 15개/페이지 */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                            gap: 2,
                        }}
                    >
                        {paginatedItems.map((e) => {
                            const isSoldOut = !e.is_unlimited && (e.stock ?? 0) <= 0;
                            const isOwned = !!e.is_owned;
                            const buyDisabled =
                                isSoldOut ||
                                isOwned ||
                                (userInfo?.points ?? 0) < e.price ||
                                buyLoadingId === e.id;

                            return (
                                <Card
                                    key={e.id}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        position: 'relative',
                                        opacity: isSoldOut ? 0.55 : 1,
                                        minWidth: 0,
                                    }}
                                >
                                    {/* 배지 */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 10,
                                            right: 10,
                                            zIndex: 2,
                                            display: 'flex',
                                            gap: 0.6,
                                        }}
                                    >
                                        {isSoldOut && <Chip size="small" label="품절" />}
                                        {isOwned && <Chip size="small" label="보유중" />}
                                    </Box>

                                    {renderThumb(e, 110)}

                                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                        {/* 이름 2줄 고정 */}
                                        <Typography
                                            align="center"
                                            sx={{
                                                fontWeight: 700,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                lineHeight: '1.2em',
                                                minHeight: '2.4em',
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {e.name}
                                        </Typography>

                                        <Typography align="center" variant="body2" color="text.secondary">
                                            {e.price} 🌿
                                        </Typography>

                                        <Typography align="center" variant="body2" color="text.secondary">
                                            {stockText(e)}
                                        </Typography>

                                        <Box mt="auto" display="flex" justifyContent="center">
                                            <Button
                                                size="small"
                                                variant="contained"
                                                disabled={buyDisabled}
                                                onClick={() => handleBuy(e.id, e.price)}
                                            >
                                                {isSoldOut ? '품절' : isOwned ? '보유중' : buyLoadingId === e.id ? '구매중…' : '구매'}
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Box>

                    {/* ✅ DESKTOP 페이지네이션 */}
                    {totalPages > 1 && (
                        <Stack mt={3} alignItems="center">
                            <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
                        </Stack>
                    )}
                </>
            )}
        </Box>
    );
};

export default EmoticonStore;
